/**
 * History Store
 * Unified undo/redo system for templateStore and animationStore
 * Uses JSON Patches for memory-efficient change tracking
 */

import { create } from 'zustand';
import { enablePatches, produceWithPatches, applyPatches, type Patch } from 'immer';
import { nanoid } from 'nanoid';
import type {
  HistoryEntry,
  HistoryTransaction,
  DesignSnapshot,
  PatchOperation,
  HistoryState,
} from '@/types/history';
import { MAX_HISTORY_ENTRIES } from '@/types/history';

// Enable Immer patches
enablePatches();

// ============================================================================
// TYPES
// ============================================================================

interface HistoryActions {
  /**
   * Get current snapshot of design state from stores
   */
  getSnapshot: () => DesignSnapshot;

  /**
   * Set the snapshot getter (called during store initialization)
   */
  setSnapshotGetter: (getter: () => DesignSnapshot) => void;

  /**
   * Set the snapshot applier (called during store initialization)
   */
  setSnapshotApplier: (applier: (snapshot: DesignSnapshot) => void) => void;

  /**
   * Record a change with a descriptive label
   * Call this after making changes to design state
   */
  recordChange: (label: string) => void;

  /**
   * Start a transaction for batching operations (e.g., drag)
   */
  startTransaction: (label: string) => void;

  /**
   * Commit the current transaction as a single history entry
   */
  commitTransaction: () => void;

  /**
   * Cancel the current transaction without recording
   */
  cancelTransaction: () => void;

  /**
   * Undo the last change
   */
  undo: () => void;

  /**
   * Redo the next change
   */
  redo: () => void;

  /**
   * Check if undo is available
   */
  canUndo: () => boolean;

  /**
   * Check if redo is available
   */
  canRedo: () => boolean;

  /**
   * Get the label of the action that would be undone
   */
  getUndoLabel: () => string | null;

  /**
   * Get the label of the action that would be redone
   */
  getRedoLabel: () => string | null;

  /**
   * Clear all history (e.g., when loading a new template)
   */
  clearHistory: () => void;

  /**
   * Take a snapshot without recording (for transaction start)
   */
  takeSnapshot: () => DesignSnapshot;
}

// Internal state for snapshot management
let snapshotGetter: (() => DesignSnapshot) | null = null;
let snapshotApplier: ((snapshot: DesignSnapshot) => void) | null = null;
let lastSnapshot: DesignSnapshot | null = null;

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Convert Immer patches to our PatchOperation format
 */
function convertPatches(patches: Patch[]): PatchOperation[] {
  return patches.map((p) => ({
    op: p.op as 'add' | 'remove' | 'replace',
    path: p.path,
    value: p.value,
  }));
}

/**
 * Create a deep clone of a snapshot
 */
function cloneSnapshot(snapshot: DesignSnapshot): DesignSnapshot {
  return JSON.parse(JSON.stringify(snapshot));
}

// ============================================================================
// STORE
// ============================================================================

const initialState: HistoryState = {
  entries: [],
  currentIndex: -1,
  activeTransaction: null,
  maxEntries: MAX_HISTORY_ENTRIES,
};

export const useHistoryStore = create<HistoryState & HistoryActions>()((set, get) => ({
  ...initialState,

  getSnapshot: () => {
    if (!snapshotGetter) {
      return { layers: [], variables: [], timeline: null };
    }
    return snapshotGetter();
  },

  setSnapshotGetter: (getter) => {
    snapshotGetter = getter;
    // Take initial snapshot
    lastSnapshot = cloneSnapshot(getter());
  },

  setSnapshotApplier: (applier) => {
    snapshotApplier = applier;
  },

  recordChange: (label) => {
    const { activeTransaction } = get();

    // If in a transaction, don't record individual changes
    if (activeTransaction) {
      return;
    }

    if (!snapshotGetter || !lastSnapshot) {
      return;
    }

    const currentSnapshot = snapshotGetter();

    // Use Immer to compute patches
    const [, forwardPatches, reversePatches] = produceWithPatches(
      lastSnapshot,
      (draft) => {
        // Copy current state to draft
        draft.layers = currentSnapshot.layers;
        draft.variables = currentSnapshot.variables;
        draft.timeline = currentSnapshot.timeline;
      }
    );

    // Skip if no actual changes
    if (forwardPatches.length === 0) {
      return;
    }

    const entry: HistoryEntry = {
      id: nanoid(),
      label,
      timestamp: Date.now(),
      forwardPatches: convertPatches(forwardPatches),
      reversePatches: convertPatches(reversePatches),
    };

    set((state) => {
      // Remove any redo history when recording new change
      const newEntries = state.entries.slice(0, state.currentIndex + 1);
      newEntries.push(entry);

      // Trim if exceeding max size
      while (newEntries.length > state.maxEntries) {
        newEntries.shift();
      }

      return {
        entries: newEntries,
        currentIndex: newEntries.length - 1,
      };
    });

    // Update last snapshot
    lastSnapshot = cloneSnapshot(currentSnapshot);
  },

  startTransaction: (label) => {
    const { activeTransaction } = get();

    // Don't start a new transaction if one is active
    if (activeTransaction) {
      return;
    }

    if (!snapshotGetter) {
      return;
    }

    const startSnapshot = cloneSnapshot(snapshotGetter());

    set({
      activeTransaction: {
        id: nanoid(),
        label,
        startSnapshot,
      },
    });
  },

  commitTransaction: () => {
    const { activeTransaction } = get();

    if (!activeTransaction || !snapshotGetter || !snapshotApplier) {
      set({ activeTransaction: null });
      return;
    }

    const currentSnapshot = snapshotGetter();
    const { startSnapshot, label } = activeTransaction;

    // Use Immer to compute patches between start and end
    const [, forwardPatches, reversePatches] = produceWithPatches(
      startSnapshot,
      (draft) => {
        draft.layers = currentSnapshot.layers;
        draft.variables = currentSnapshot.variables;
        draft.timeline = currentSnapshot.timeline;
      }
    );

    // Skip if no actual changes
    if (forwardPatches.length === 0) {
      set({ activeTransaction: null });
      lastSnapshot = cloneSnapshot(currentSnapshot);
      return;
    }

    const entry: HistoryEntry = {
      id: nanoid(),
      label,
      timestamp: Date.now(),
      forwardPatches: convertPatches(forwardPatches),
      reversePatches: convertPatches(reversePatches),
    };

    set((state) => {
      // Remove any redo history when recording new change
      const newEntries = state.entries.slice(0, state.currentIndex + 1);
      newEntries.push(entry);

      // Trim if exceeding max size
      while (newEntries.length > state.maxEntries) {
        newEntries.shift();
      }

      return {
        entries: newEntries,
        currentIndex: newEntries.length - 1,
        activeTransaction: null,
      };
    });

    // Update last snapshot
    lastSnapshot = cloneSnapshot(currentSnapshot);
  },

  cancelTransaction: () => {
    const { activeTransaction } = get();

    if (!activeTransaction || !snapshotApplier) {
      set({ activeTransaction: null });
      return;
    }

    // Restore to start snapshot
    snapshotApplier(activeTransaction.startSnapshot);
    lastSnapshot = cloneSnapshot(activeTransaction.startSnapshot);

    set({ activeTransaction: null });
  },

  undo: () => {
    const { entries, currentIndex } = get();

    if (currentIndex < 0 || !snapshotGetter || !snapshotApplier) {
      return;
    }

    const entry = entries[currentIndex];
    if (!entry) {
      return;
    }

    // Apply reverse patches
    const currentSnapshot = snapshotGetter();
    const newSnapshot = applyPatches(currentSnapshot, entry.reversePatches as Patch[]);

    snapshotApplier(newSnapshot);
    lastSnapshot = cloneSnapshot(newSnapshot);

    set({ currentIndex: currentIndex - 1 });
  },

  redo: () => {
    const { entries, currentIndex } = get();

    if (currentIndex >= entries.length - 1 || !snapshotGetter || !snapshotApplier) {
      return;
    }

    const entry = entries[currentIndex + 1];
    if (!entry) {
      return;
    }

    // Apply forward patches
    const currentSnapshot = snapshotGetter();
    const newSnapshot = applyPatches(currentSnapshot, entry.forwardPatches as Patch[]);

    snapshotApplier(newSnapshot);
    lastSnapshot = cloneSnapshot(newSnapshot);

    set({ currentIndex: currentIndex + 1 });
  },

  canUndo: () => {
    const { currentIndex } = get();
    return currentIndex >= 0;
  },

  canRedo: () => {
    const { entries, currentIndex } = get();
    return currentIndex < entries.length - 1;
  },

  getUndoLabel: () => {
    const { entries, currentIndex } = get();
    if (currentIndex < 0) return null;
    return entries[currentIndex]?.label ?? null;
  },

  getRedoLabel: () => {
    const { entries, currentIndex } = get();
    if (currentIndex >= entries.length - 1) return null;
    return entries[currentIndex + 1]?.label ?? null;
  },

  clearHistory: () => {
    if (snapshotGetter) {
      lastSnapshot = cloneSnapshot(snapshotGetter());
    }
    set({
      entries: [],
      currentIndex: -1,
      activeTransaction: null,
    });
  },

  takeSnapshot: () => {
    if (!snapshotGetter) {
      return { layers: [], variables: [], timeline: null };
    }
    return cloneSnapshot(snapshotGetter());
  },
}));

// ============================================================================
// SELECTOR HOOKS
// ============================================================================

export const useCanUndo = () => useHistoryStore((s) => s.currentIndex >= 0);
export const useCanRedo = () => useHistoryStore((s) => s.currentIndex < s.entries.length - 1);
export const useUndoLabel = () => useHistoryStore((s) =>
  s.currentIndex >= 0 ? s.entries[s.currentIndex]?.label ?? null : null
);
export const useRedoLabel = () => useHistoryStore((s) =>
  s.currentIndex < s.entries.length - 1 ? s.entries[s.currentIndex + 1]?.label ?? null : null
);
export const useIsInTransaction = () => useHistoryStore((s) => s.activeTransaction !== null);
